/* eslint-disable react/forbid-prop-types */
import { useRef, useLayoutEffect, useEffect } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import PlusCourseCard from '@comp/PlusCourseCard/PlusCourseCard';
import Carousel from '@comp/Carousel/Carousel';
import useWindowSize from '@hooks/useWindowSize';
import { breakpoints, mediaQuery, Spacings } from '@styles/index';
import useEnroll from '@hooks/useEnroll';
import { segment } from '@actions/analytics';
import { COURSE_SIGNUP_BANNER } from '@comp/SignupGenericBanner/constant';
import useFollowGoal from '@api/hooks/useFollowGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import Dynamic from '@base/Dynamic/Dynamic';
import FreeLessonList from './FreeLessonList';
import LessonDetails from './LessonDetails';
import Banner from './Banner';

const Dialog = Dynamic(() => import('@comp/Dialog/Dialog'));
const SignupGenericBanner = Dynamic(() =>
  import('@comp/SignupGenericBanner/SignupGenericBanner')
);

const StyledBanner = styled(Banner)`
  margin-bottom: 36px;
`;

const StyledCarousel = styled(Carousel)`
  margin-top: 40px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: 56px;
  }
`;

const StyledSignupBanner = styled(SignupGenericBanner)`
  margin-top: ${Spacings.SPACING_12B};
  grid-column: 1 / -1;

  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const message =
  'This course will be removed from your schedule and you will stop receiving updates related to this course';

const FreeLesson = ({
  freeLessonList,
  freeLessonInfo,
  courseInfo,
  freeRelatedCourses
}) => {
  const { isMobile } = useWindowSize();
  const lessonDetailsRef = useRef(null);
  const lessonListRef = useRef(null);
  const { isUnEnrollModalOpen, toggleEnroll, acceptOnClick, onCloseModal } =
    useEnroll({
      dataAnalytics: '',
      lastPrimarySource: '',
      lastPrimarySourceSection: ''
    });
  const goal = courseInfo?.goal ?? {};
  const { followCurrentGoal } = useFollowGoal(goal.uid);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const followCurrentGoalRef = useRef(null);
  const isDemo = !freeLessonList?.length;
  useLayoutEffect(() => {
    if (lessonListRef.current) {
      if (isMobile) lessonListRef.current.style.height = 'auto';
      else
        lessonListRef.current.style.height = `${lessonDetailsRef.current.clientHeight}px`;
    }
  }, [isMobile]);
  useEffect(() => {
    let timer = null;
    if (isLoggedIn) {
      followCurrentGoalRef.current = followCurrentGoal;
      timer = setTimeout(() => {
        followCurrentGoalRef?.current();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, followCurrentGoal]);

  if (!freeLessonInfo && !freeLessonList) return null;
  const { author, uid, name, programme } = freeLessonInfo;
  const learnMoreClick = () => {
    segment.track('SEO - Goal Learn More Clicked', {
      educator_uid: author.uid,
      educator_username: author.username,
      last_primary_source: 'lesson page',
      free_course_uid: programme.course.uid,
      free_course_title: programme.course.name,
      free_class_uid: uid,
      free_class_title: name
    });
  };
  return (
    <>
      <Dialog
        onClose={onCloseModal}
        dialogState={isUnEnrollModalOpen}
        heading="Are you sure you want to unenroll?"
        message={message}
        acceptLabel="Unenroll"
        rejectLabel="Cancel"
        acceptOnClick={acceptOnClick}
        rejectOnClick={onCloseModal}
      />
      <LessonDetails
        freeLessonList={freeLessonList}
        freeLessonInfo={freeLessonInfo}
        courseInfo={courseInfo}
        isDemo={isDemo}
        ref={lessonDetailsRef}
      />
      {freeLessonList.length ? (
        <FreeLessonList
          freeLessonList={freeLessonList}
          freeLessonInfo={freeLessonInfo}
          ref={lessonListRef}
        />
      ) : null}
      <StyledSignupBanner
        data={COURSE_SIGNUP_BANNER}
        goalUID={courseInfo?.goal.uid}
      />
      <StyledBanner goal={courseInfo.goal} onClick={learnMoreClick} />
      {freeRelatedCourses?.map((similarCourse, index) => {
        const blockTitle = similarCourse?.extraBlockInfo?.title || '';
        return (
          <StyledCarousel
            label={blockTitle}
            key={blockTitle}
            showControls={false}
            numberOfItems={4}
            gridColumn="span 24"
            $last={index === freeRelatedCourses.length - 1}
          >
            {similarCourse?.data?.map((courseData) => (
              <PlusCourseCard
                gridColumn="span 6/ auto"
                key={courseData.uid}
                course={{
                  ...courseData,
                  progress: {
                    nextLessonRelativeLink:
                      courseData?.nextSession?.relativeLink ||
                      courseData?.relativeLink
                  },
                  nextTrack:
                    courseData.nextTrack ||
                    courseData.nextSession ||
                    courseData.nextClass ||
                    courseData.nextItem,
                  language:
                    courseData?.languageDisplay ||
                    courseData?.multipleLanguageDisplay?.[0]
                }}
                toggleEnroll={toggleEnroll(courseData)}
                lastPrimarySource="lesson page"
                label={blockTitle}
                index={index}
              />
            ))}
          </StyledCarousel>
        );
      })}
    </>
  );
};

export default FreeLesson;
FreeLesson.propTypes = {
  freeLessonList: PropTypes.arrayOf(PropTypes.any),
  freeLessonInfo: PropTypes.objectOf(PropTypes.any),
  courseInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  freeRelatedCourses: PropTypes.arrayOf(PropTypes.any)
};

FreeLesson.defaultProps = {
  freeLessonList: null,
  freeLessonInfo: null,
  freeRelatedCourses: []
};
